"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * FAQ accordion (item 11): eyebrow copy changed from "Questions,
 * answered" to "Questions? Answered." per the owner's punch-list item 20
 * — the question mark makes the eyebrow read as a mini-conversation
 * instead of a label. Native `<details>`/`<summary>` widgets stay
 * (keyboard- and screen-reader-accessible without extra JS). The
 * employer-visibility FAQ item is restored (q5/a5) now that the employer
 * section (item 10) is back.
 *
 * ADR-085 IAP-4: `a4` is untrue today (no plan carries a trial since ADR-083); the corrected `a4AppStore` ships with the build-time
 * `VITE_BILLING_MODE=app_store` copy (lib/billingMode.ts). The live (`whop`) sentence waits for the founder's go (OWNER_REVIEW O-31).
 */

import { useI18n } from "@/i18n"
import { BILLING_MODE } from "@/lib/billingMode"
import { Reveal } from "./Reveal"

export function FaqSection() {
  const { t } = useI18n()
  const appStore = BILLING_MODE === "app_store"

  const items = [
    { question: t("landingPage.faq.q1"), answer: t("landingPage.faq.a1") },
    { question: t("landingPage.faq.q2"), answer: t("landingPage.faq.a2") },
    { question: t("landingPage.faq.q3"), answer: t("landingPage.faq.a3") },
    {
      question: appStore ? t("landingPage.faq.q4AppStore") : t("landingPage.faq.q4"),
      answer: appStore ? t("landingPage.faq.a4AppStore") : t("landingPage.faq.a4"),
    },
    { question: t("landingPage.faq.q5"), answer: t("landingPage.faq.a5") },
  ]

  return (
    <section id="faq" className="scroll-mt-[12vh] border-b border-line bg-cream">
      {/* Craft fix C8 (2026-09-10): the block used a narrower container than
          every other section, so its heading was visibly indented; the answers
          keep their comfortable measure via max-w-3xl on the list itself. */}
      <div className="mx-auto max-w-6xl px-4 py-16 md:px-8">
        <Reveal>
          <p className="text-sm font-semibold tracking-wide text-green uppercase">
            {t("landingPage.faq.eyebrow")}
          </p>
          <h2 className="mt-2 text-3xl font-bold text-ink md:text-4xl">
            {t("landingPage.faq.heading")}
          </h2>
        </Reveal>

        <Reveal index={1} className="mt-8 flex max-w-3xl flex-col gap-3">
          {items.map(item => (
            <details
              key={item.question}
              className="group rounded-bb-lg border border-line bg-paper p-4 open:pb-4"
            >
              <summary className="min-h-11 cursor-pointer list-none text-base font-semibold text-ink marker:content-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green">
                <span className="flex items-center justify-between gap-3">
                  <span>{item.question}</span>
                  <span aria-hidden="true" className="shrink-0 text-ink-2 group-open:rotate-45">
                    +
                  </span>
                </span>
              </summary>
              <p className="mt-2 text-sm text-ink-2">{item.answer}</p>
            </details>
          ))}
        </Reveal>
      </div>
    </section>
  )
}
